import { Box, Button, ButtonBase, Typography } from "@mui/material";

import type { HomeSummary } from "../types/home.types";
import {
  agendaDayLabel,
  formatShortDay,
  saoPauloDayKey,
} from "../utils/homeFilters";
import { NoApprovalChip, StatusChip } from "./HomeChip";
import { HomePanel } from "./HomePanel";
import { HOME_COLORS } from "./homeTheme";

interface NextSevenDaysProps {
  today?: string;
  agenda?: HomeSummary["agenda"];
  onOpenProject(projectId: string): void;
  onSeeAll(): void;
}

const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

export function NextSevenDays({
  today,
  agenda,
  onOpenProject,
  onSeeAll,
}: NextSevenDaysProps) {
  const items = agenda?.items ?? [];
  const total = agenda?.total ?? 0;

  return (
    <HomePanel
      title="Próximos 7 dias"
      aside={
        agenda ? `Mostrando ${items.length} de ${total} eventos` : undefined
      }
      flex="1 1 420px"
    >
      <Box display="flex" flexDirection="column">
        {agenda && items.length === 0 && (
          <Typography sx={{ py: 3, color: HOME_COLORS.muted, fontSize: 14 }}>
            Nenhum evento começa nos próximos 7 dias.
          </Typography>
        )}
        {items.map((item) => {
          const dayKey = saoPauloDayKey(item.startDate);
          return (
            <ButtonBase
              key={item.id}
              onClick={() => onOpenProject(item.id)}
              sx={{
                display: "grid",
                gridTemplateColumns: "64px minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 1.5,
                py: 1.25,
                px: 1,
                textAlign: "left",
                borderBottom: `1px solid ${HOME_COLORS.line}`,
                "&:hover": { bgcolor: HOME_COLORS.soft },
              }}
            >
              <Box display="flex" flexDirection="column">
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {today ? agendaDayLabel(dayKey, today) : ""}
                </Typography>
                <Typography sx={{ fontSize: 12, color: HOME_COLORS.muted }}>
                  {formatShortDay(dayKey)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" minWidth={0}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, ...ellipsis }}>
                  {item.mmCode}/{item.yearCode} · {item.eventName ?? "---"}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: HOME_COLORS.muted, ...ellipsis }}
                >
                  {item.clientName ?? "---"}
                  {item.coordinatorName
                    ? ` · Coord. ${item.coordinatorName}`
                    : ""}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={0.5}
                flexWrap="wrap"
                justifyContent="flex-end"
              >
                {item.semAprovacao && <NoApprovalChip />}
                <StatusChip status={item.status} />
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
      <Button
        variant="outlined"
        onClick={onSeeAll}
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        Ver todos na lista de Projetos
      </Button>
    </HomePanel>
  );
}
